import { useRef, useState } from "react";
import { TRANSFER_DISCUSSION_REFUSAL, TRANSFER_FAILED } from "@omnabase/shared";
import type { Attachment } from "@omnabase/ui";
import { api } from "./api";
import { transferLine } from "./transferWords";

/** A reply composer's transfers (the files round, 2026-10-30; brief rule 1): each big file uploads at once with its progress line; the loop
 *  (`transferUpload.ts`) is loaded only now, with a dynamic import. The cross cancels an upload (or drops a finished transfer: the server
 *  frees its room); Send takes the finished ones and waits for none. */
type Item = { key: number; name: string; size: number; state: "uploading" | "done" | "failed"; progress: number; id?: string; stop: AbortController };
export type Transfers = { cards: Attachment[]; /** `privately` (the To line round): this reply goes by mail, so no ticket is named and a public topic refuses nothing */ add: (file: File, o?: { privately?: boolean }) => void; remove: (index: number) => void; ids: () => string[]; uploading: () => boolean; reset: () => void };

/** `refused` (the To line round, 2026-10-30): a refusal at the start the page answers itself (true), e.g. a public topic's with "Send by mail instead" */
export function useTransfers(slug: string, o: { ticket?: number; say: (text: string) => void; refused?: (file: File, message: string) => boolean }): Transfers {
  const [items, setItems] = useState<Item[]>([]);
  const latest = useRef<Item[]>([]); latest.current = items;
  const seq = useRef(0);
  const patch = (key: number, p: Partial<Item>) => setItems((cur) => cur.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const base = `/api/v1/w/${slug}/transfers`;

  const add = (file: File, how: { privately?: boolean } = {}) => {
    const item: Item = { key: ++seq.current, name: file.name, size: file.size, state: "uploading", progress: 0, stop: new AbortController() };
    setItems((cur) => [...cur, item]);
    void (async () => {
      try {
        const { uploadTransfer } = await import("./transferUpload");
        const done = await uploadTransfer({ slug, file, ticket: how.privately ? undefined : o.ticket, signal: item.stop.signal, onStarted: (id) => patch(item.key, { id }), onProgress: (share) => patch(item.key, { progress: share }) });
        patch(item.key, { state: "done", progress: 1, id: done.id });
      } catch (e) {
        if (item.stop.signal.aborted) return;
        const message = (e as Error).message;
        if (message !== TRANSFER_FAILED) { if (!o.refused?.(file, message)) o.say(`${file.name}: ${message}`); setItems((cur) => cur.filter((x) => x.key !== item.key)); return; } // refused at the start: the server's words (the room, a public topic)
        patch(item.key, { state: "failed" });
      }
    })();
  };
  const remove = (index: number) => {
    const item = latest.current[index]; if (!item) return;
    item.stop.abort();
    if (item.id) void api.del(`${base}/${item.id}`).catch(() => undefined); // gone already (swept) is fine
    setItems((cur) => cur.filter((x) => x.key !== item.key));
  };
  return {
    cards: items.map((x) => ({ kind: "file", name: x.name, line: transferLine(x), ...(x.state === "uploading" ? { progress: x.progress } : {}) })),
    add, remove,
    ids: () => latest.current.filter((x) => x.state === "done" && x.id).map((x) => x.id!),
    uploading: () => latest.current.some((x) => x.state === "uploading"),
    reset: () => setItems([]),
  };
}

/** what Send says instead of sending: a line of the ticket's discussion never carries a transfer (ticket discussion round two; a note had the
 *  same rule); an upload still running is waited for */
export function transferRefusal(discussion: boolean, t: Pick<Transfers, "ids" | "uploading">): string | null {
  if (discussion && (t.ids().length || t.uploading())) return TRANSFER_DISCUSSION_REFUSAL;
  return t.uploading() ? "Wait for the upload to finish, then send" : null;
}
